import SignatureCanvas from "react-signature-canvas";
import { useRef, useEffect, useState } from "react";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import "./Formulario.css";

function Formulario({ onCambiarVista }) {
  const [form, setForm] = useState({
    email: "",
    numero_identificacion: "",
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    id_tipoIdentificacion: "",
    id_estadoCivil: "",
    id_gradoEscolaridad: "",
    telefono_celular: "",
    id_Dpto: "",
    id_Mupio: "",
    Barrio: "",
    direccion: "",
    id_sedeEntidad: "",
    id_cargo: "",
    areaServicio: "",
    id_modoContratacion: "",
    tiempoEmpresa: "",
    firma: "",
  });

  const [listas, setListas] = useState({
    cargos: [],
    departamentos: [],
    municipios: [],
    sedes: [],
    estados: [],
    escolaridades: [],
    documentos: [],
    contrataciones: [],
  });

  const [firmaCompletada, setFirmaCompletada] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const sigCanvas = useRef(null);
  const sigWrapper = useRef(null);

  // ✅ Obtener fecha actual en formato YYYY-MM-DD
  const obtenerFechaActual = () => {
    const hoy = new Date();
    const año = hoy.getFullYear();
    const mes = String(hoy.getMonth() + 1).padStart(2, "0");
    const dia = String(hoy.getDate()).padStart(2, "0");
    return `${año}-${mes}-${dia}`;
  };

  // ✅ Cargar listas dinámicas
  useEffect(() => {
    const cargarListas = async () => {
      try {
        const baseURL = "https://sindicato.sintrasecsalud.com/api/index.php?modulo=listas&accion=";
        
        const respuestas = await Promise.all([
          fetch(baseURL + "cargos"),
          fetch(baseURL + "departamentos"),
          fetch(baseURL + "municipios"),
          fetch(baseURL + "sedes"),
          fetch(baseURL + "estadosCiviles"),
          fetch(baseURL + "escolaridades"),
          fetch(baseURL + "documentos"),
          fetch(baseURL + "contratacion"),
        ]);

        for (const r of respuestas) {
          if (!r.ok) {
            console.error("Error HTTP:", r.status, r.url);
            throw new Error(`Error HTTP ${r.status}`);
          }
        }

        const [
          cargos,
          departamentos,
          municipios,
          sedes,
          estados,
          escolaridades,
          documentos,
          contrataciones,
        ] = await Promise.all(respuestas.map((r) => r.json()));

        setListas({
          cargos: Array.isArray(cargos) ? cargos : [],
          departamentos: Array.isArray(departamentos) ? departamentos : [],
          municipios: Array.isArray(municipios) ? municipios : [],
          sedes: Array.isArray(sedes) ? sedes : [],
          estados: Array.isArray(estados) ? estados : [],
          escolaridades: Array.isArray(escolaridades) ? escolaridades : [],
          documentos: Array.isArray(documentos) ? documentos : [],
          contrataciones: Array.isArray(contrataciones) ? contrataciones : [],
        });
      } catch (error) {
        console.error("Error cargando listas:", error);
        Swal.fire({
          icon: "error",
          title: "Error cargando listas",
          text: error.message,
        });
      }
    };
    cargarListas();
  }, []);

  // Ajustar canvas de firma
  useEffect(() => {
    const resizeCanvas = () => {
      if (sigCanvas.current && sigWrapper.current) {
        const canvas = sigCanvas.current.getCanvas();
        canvas.width = sigWrapper.current.offsetWidth;
        canvas.height = 200;
      }
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  // ✅ FUNCIÓN: Validar solo letras (incluye acentos y ñ)
  const soloLetras = (valor) => {
    return valor.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "");
  };

  // ✅ FUNCIÓN: Validar solo números
  const soloNumeros = (valor) => {
    return valor.replace(/[^0-9]/g, "");
  };

  // ✅ FUNCIÓN: Validar letras y números (para barrio)
  const letrasYNumeros = (valor) => {
    return valor.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ0-9\s]/g, "");
  };

  // ✅ FUNCIÓN: Validar email
  const validarEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // ✅ HANDLE CHANGE CON VALIDACIONES
  const handleChange = (e) => {
    const { name, value } = e.target;
    let valorLimpio = value;

    switch (name) {
      case "primerNombre":
      case "segundoNombre":
      case "primerApellido":
      case "segundoApellido":
        valorLimpio = soloLetras(value);
        break;
      
      case "numero_identificacion":
      case "telefono_celular":
        valorLimpio = soloNumeros(value);
        break;
      
      case "tiempoEmpresa":
        // Solo números de hasta 2 dígitos
        valorLimpio = soloNumeros(value).slice(0, 2);
        break;
      
      case "Barrio":
      case "areaServicio":
        valorLimpio = letrasYNumeros(value);
        break;
      
      case "email":
      case "direccion":
        // Sin validación de caracteres especiales
        valorLimpio = value;
        break;
      
      default:
        valorLimpio = value;
    }

    setForm({ ...form, [name]: valorLimpio });
  };

  const guardarFirma = () => {
    const canvas = sigCanvas.current.getCanvas();
    if (sigCanvas.current.isEmpty()) {
      Swal.fire({
        icon: "warning",
        title: "Firma vacía",
        text: "Debe firmar antes de guardar",
      });
      return;
    }
    const firmaBase64 = canvas.toDataURL("image/png");
    setForm({ ...form, firma: firmaBase64 });
    setFirmaCompletada(true);
    Swal.fire({
      icon: "success",
      title: "Firma guardada",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const limpiarFirma = () => {
    sigCanvas.current.clear();
    setForm({ ...form, firma: "" });
    setFirmaCompletada(false);
  };

  // GENERAR PDF
  const generarPDF = async () => {
    setGenerandoPDF(true);
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFillColor(0, 51, 102);
    pdf.rect(0, 0, pageWidth, 45, "F");
    pdf.setFillColor(212, 175, 55);
    pdf.rect(0, 45, pageWidth, 2, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(22);
    pdf.setFont("helvetica", "bold");
    pdf.text("SINTRASECSALUD", pageWidth / 2, 18, { align: "center" });

    pdf.setFontSize(14);
    pdf.setFont("helvetica", "normal");
    pdf.text("FORMULARIO DE AFILIACIÓN SINDICAL", pageWidth / 2, 28, {
      align: "center",
    });

    pdf.setFontSize(10);
    pdf.text(
      `Fecha de registro: ${new Date().toLocaleString("es-CO")}`,
      pageWidth / 2,
      38,
      { align: "center" }
    );

    let yPos = 55;
    const margenIzq = 15;
    const anchoLabel = 55;

    const getDescripcion = (lista, id, campoId, campoDesc) => {
      const item = lista.find((i) => String(i[campoId]) === String(id));
      return item ? item[campoDesc] : id || "No especificado";
    };

    const escribirCampo = (label, valor, y) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(0, 51, 102);
      pdf.text(label + ":", margenIzq, y);

      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(50, 50, 50);
      pdf.text(String(valor || ""), margenIzq + anchoLabel, y);
      return y + 7;
    };

    const escribirSeccion = (titulo, y) => {
      pdf.setFillColor(0, 51, 102);
      pdf.rect(margenIzq, y - 4, pageWidth - margenIzq * 2, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.text(titulo, margenIzq + 3, y + 2);
      return y + 12;
    };

    yPos = escribirSeccion("DATOS PERSONALES", yPos);
    yPos = escribirCampo("Primer Nombre", form.primerNombre, yPos);
    yPos = escribirCampo("Segundo Nombre", form.segundoNombre, yPos);
    yPos = escribirCampo("Primer Apellido", form.primerApellido, yPos);
    yPos = escribirCampo("Segundo Apellido", form.segundoApellido, yPos);
    yPos = escribirCampo(
      "Tipo Documento",
      getDescripcion(
        listas.documentos,
        form.id_tipoIdentificacion,
        "id_tipoIdentificacion",
        "descIdentificacion"
      ),
      yPos
    );
    yPos = escribirCampo("Número Documento", form.numero_identificacion, yPos);
    yPos = escribirCampo(
      "Estado Civil",
      getDescripcion(
        listas.estados,
        form.id_estadoCivil,
        "id_estadoCivil",
        "descEstadoCivil"
      ),
      yPos
    );
    yPos = escribirCampo(
      "Escolaridad",
      getDescripcion(
        listas.escolaridades,
        form.id_gradoEscolaridad,
        "id_gradoEscolaridad",
        "descEscolaridad"
      ),
      yPos
    );
    yPos = escribirCampo("Email", form.email, yPos);
    yPos = escribirCampo("Celular", form.telefono_celular, yPos);

    yPos = escribirSeccion("UBICACIÓN", yPos + 3);
    yPos = escribirCampo(
      "Departamento",
      getDescripcion(listas.departamentos, form.id_Dpto, "id_Dpto", "descDpto"),
      yPos
    );
    yPos = escribirCampo(
      "Municipio",
      getDescripcion(
        listas.municipios,
        form.id_Mupio,
        "id_Mupio",
        "descMupio"
      ),
      yPos
    );
    yPos = escribirCampo("Barrio", form.Barrio, yPos);
    yPos = escribirCampo("Dirección", form.direccion, yPos);

    yPos = escribirSeccion("INFORMACIÓN LABORAL", yPos + 3);
    yPos = escribirCampo(
      "Sede",
      getDescripcion(
        listas.sedes,
        form.id_sedeEntidad,
        "id_sedeEntidad",
        "descSede"
      ),
      yPos
    );
    yPos = escribirCampo(
      "Cargo",
      getDescripcion(listas.cargos, form.id_cargo, "id_cargo", "descCargo"),
      yPos
    );
    yPos = escribirCampo("Área de Servicio", form.areaServicio, yPos);
    yPos = escribirCampo(
      "Modo Contratación",
      getDescripcion(
        listas.contrataciones,
        form.id_modoContratacion,
        "id_modoContratacion",
        "descMcontratacion"
      ),
      yPos
    );
    yPos = escribirCampo("Tiempo en Empresa", form.tiempoEmpresa + " años", yPos);
    yPos = escribirCampo("Fecha Ingreso Sindicato", obtenerFechaActual(), yPos);

    yPos = escribirSeccion("FIRMA DEL AFILIADO", yPos + 3);
    if (form.firma) {
      pdf.text("Firma registrada:", margenIzq, yPos);
      yPos += 5;
      pdf.addImage(form.firma, "PNG", margenIzq, yPos, 70, 30);
      yPos += 35;
    }

    pdf.setFillColor(0, 51, 102);
    pdf.rect(0, 280, pageWidth, 17, "F");
    pdf.setFillColor(212, 175, 55);
    pdf.rect(0, 278, pageWidth, 2, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.text(
      "SINTRASECSALUD - Sindicato de Trabajadores",
      pageWidth / 2,
      287,
      { align: "center" }
    );
    pdf.text(
      "Documento generado electrónicamente - Válido sin firma física",
      pageWidth / 2,
      293,
      { align: "center" }
    );

    const nombreArchivo = `Afiliacion_${form.primerNombre}_${form.primerApellido}_${form.numero_identificacion}.pdf`;
    pdf.save(nombreArchivo);
    setGenerandoPDF(false);
    return pdf;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones antes de enviar
    if (!validarEmail(form.email)) {
      Swal.fire({
        icon: "error",
        title: "Email inválido",
        text: "Por favor ingrese un correo electrónico válido",
      });
      return;
    }

    if (form.telefono_celular.length < 7) {
      Swal.fire({
        icon: "error",
        title: "Teléfono inválido",
        text: "El número de celular debe tener al menos 7 dígitos",
      });
      return;
    }

    if (form.numero_identificacion.length < 5) {
      Swal.fire({
        icon: "error",
        title: "Documento inválido",
        text: "El número de identificación debe tener al menos 5 dígitos",
      });
      return;
    }

    if (!form.tiempoEmpresa) {
      Swal.fire({
        icon: "error",
        title: "Tiempo requerido",
        text: "Debe ingresar el tiempo en la empresa",
      });
      return;
    }

    if (!firmaCompletada && !form.firma) {
      Swal.fire({
        icon: "warning",
        title: "Firma requerida",
        text: "Debe firmar el formulario antes de enviarlo",
      });
      return;
    }

    // ✅ Agregar fecha de ingreso sindicato (fecha actual)
    let formFinal = { 
      ...form, 
      fechaIngresoSindicato: obtenerFechaActual() 
    };
    
    if (!formFinal.firma) {
      const canvas = sigCanvas.current.getCanvas();
      formFinal.firma = canvas.toDataURL("image/png");
    }

    try {
      const res = await fetch(
        "https://sindicato.sintrasecsalud.com/api/index.php?modulo=afiliacion&accion=guardar",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formFinal),
        }
      );

      const data = await res.json();

      if (data.success) {
        await generarPDF();
        Swal.fire({
          icon: "success",
          title: "¡Afiliación exitosa!",
          text: "Su formulario ha sido registrado y descargado correctamente",
          timer: 3000,
          showConfirmButton: false,
        });

        setForm({
          email: "",
          numero_identificacion: "",
          primerNombre: "",
          segundoNombre: "",
          primerApellido: "",
          segundoApellido: "",
          id_tipoIdentificacion: "",
          id_estadoCivil: "",
          id_gradoEscolaridad: "",
          telefono_celular: "",
          id_Dpto: "",
          id_Mupio: "",
          Barrio: "",
          direccion: "",
          id_sedeEntidad: "",
          id_cargo: "",
          areaServicio: "",
          id_modoContratacion: "",
          tiempoEmpresa: "",
          firma: "",
        });
        limpiarFirma();

        setTimeout(() => {
          if (onCambiarVista) onCambiarVista("inicio");
        }, 3000);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.message || data.error || "Error desconocido",
        });
      }
    } catch (error) {
      console.error("Error en handleSubmit:", error);
      Swal.fire({
        icon: "error",
        title: "Error de conexión",
        text: "No se pudo conectar con el servidor",
      });
    }
  };

  return (
    <div className="formulario-wrapper">
      <button
        className="btn-volver-inicio"
        onClick={() => onCambiarVista && onCambiarVista("inicio")}
        style={{
          position: "fixed",
          top: "90px",
          left: "20px",
          background: "rgba(255, 255, 255, 0.2)",
          color: "white",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          borderRadius: "50px",
          padding: "10px 20px",
          cursor: "pointer",
          zIndex: 1000,
          backdropFilter: "blur(10px)",
          transition: "all 0.3s ease",
        }}
      >
        ← Volver al Inicio
      </button>

      <div className="formulario-header">
        <div className="header-content">
          <h1> Formulario de Vinculación</h1>
          <p>SINTRASECSALUD - Sindicato de Trabajadores</p>
        </div>
        <div className="header-decoration"></div>
      </div>

      <div className="formulario-container">
        <form className="formulario-content" onSubmit={handleSubmit}>
          {/* DATOS PERSONALES */}
          <div className="form-section">
            <h2 className="section-title">
              <span className="icon"></span> Datos Personales
            </h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Primer Nombre *</label>
                <input
                  name="primerNombre"
                  value={form.primerNombre}
                  onChange={handleChange}
                  required
                  placeholder="Ingrese su primer nombre"
                  title="Solo se permiten letras"
                />
              </div>

              <div className="form-group">
                <label>Segundo Nombre</label>
                <input
                  name="segundoNombre"
                  value={form.segundoNombre}
                  onChange={handleChange}
                  placeholder="Ingrese su segundo nombre"
                  title="Solo se permiten letras"
                />
              </div>

              <div className="form-group">
                <label>Primer Apellido *</label>
                <input
                  name="primerApellido"
                  value={form.primerApellido}
                  onChange={handleChange}
                  required
                  placeholder="Ingrese su primer apellido"
                  title="Solo se permiten letras"
                />
              </div>

              <div className="form-group">
                <label>Segundo Apellido</label>
                <input
                  name="segundoApellido"
                  value={form.segundoApellido}
                  onChange={handleChange}
                  placeholder="Ingrese su segundo apellido"
                  title="Solo se permiten letras"
                />
              </div>

              <div className="form-group">
                <label>Tipo Documento *</label>
                <select
                  name="id_tipoIdentificacion"
                  value={form.id_tipoIdentificacion}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.documentos) &&
                    listas.documentos.map((d) => (
                      <option
                        key={d.id_tipoIdentificacion}
                        value={d.id_tipoIdentificacion}
                      >
                        {d.descIdentificacion}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Número Documento *</label>
                <input
                  name="numero_identificacion"
                  value={form.numero_identificacion}
                  onChange={handleChange}
                  required
                  placeholder="Número de identificación"
                  title="Solo se permiten números"
                  maxLength="15"
                />
              </div>

              <div className="form-group">
                <label>Estado Civil</label>
                <select
                  name="id_estadoCivil"
                  value={form.id_estadoCivil}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.estados) &&
                    listas.estados.map((e) => (
                      <option key={e.id_estadoCivil} value={e.id_estadoCivil}>
                        {e.descEstadoCivil}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Escolaridad</label>
                <select
                  name="id_gradoEscolaridad"
                  value={form.id_gradoEscolaridad}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.escolaridades) &&
                    listas.escolaridades.map((e) => (
                      <option
                        key={e.id_gradoEscolaridad}
                        value={e.id_gradoEscolaridad}
                      >
                        {e.descEscolaridad}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="correo@ejemplo.com"
                />
              </div>

              <div className="form-group">
                <label>Celular *</label>
                <input
                  name="telefono_celular"
                  value={form.telefono_celular}
                  onChange={handleChange}
                  required
                  placeholder="Número de celular"
                  title="Solo se permiten números"
                  maxLength="15"
                />
              </div>
            </div>
          </div>

          {/* UBICACIÓN */}
          <div className="form-section">
            <h2 className="section-title">
              <span className="icon">📍</span> Ubicación
            </h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Departamento *</label>
                <select
                  name="id_Dpto"
                  value={form.id_Dpto}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.departamentos) &&
                    listas.departamentos.map((d) => (
                      <option key={d.id_Dpto} value={d.id_Dpto}>
                        {d.descDpto}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Municipio *</label>
                <select
                  name="id_Mupio"
                  value={form.id_Mupio}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.municipios) &&
                    listas.municipios.map((m) => (
                      <option key={m.id_Mupio} value={m.id_Mupio}>
                        {m.descMupio}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Barrio</label>
                <input
                  name="Barrio"
                  value={form.Barrio}
                  onChange={handleChange}
                  placeholder="Nombre del barrio"
                  title="Solo se permiten letras y números"
                />
              </div>

              <div className="form-group full-width">
                <label>Dirección *</label>
                <input
                  name="direccion"
                  value={form.direccion}
                  onChange={handleChange}
                  required
                  placeholder="Dirección completa"
                />
              </div>
            </div>
          </div>

          {/* INFORMACIÓN LABORAL */}
          <div className="form-section">
            <h2 className="section-title">
              <span className="icon">💼</span> Información Laboral
            </h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Sede *</label>
                <select
                  name="id_sedeEntidad"
                  value={form.id_sedeEntidad}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.sedes) &&
                    listas.sedes.map((s) => (
                      <option key={s.id_sedeEntidad} value={s.id_sedeEntidad}>
                        {s.descSede}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Cargo *</label>
                <select
                  name="id_cargo"
                  value={form.id_cargo}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.cargos) &&
                    listas.cargos.map((c) => (
                      <option key={c.id_cargo} value={c.id_cargo}>
                        {c.descCargo}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Área de Servicio</label>
                <input
                  name="areaServicio"
                  value={form.areaServicio}
                  onChange={handleChange}
                  placeholder="Área o departamento"
                  title="Solo se permiten letras y números"
                />
              </div>

              <div className="form-group">
                <label>Modo Contratación</label>
                <select
                  name="id_modoContratacion"
                  value={form.id_modoContratacion}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  {Array.isArray(listas.contrataciones) &&
                    listas.contrataciones.map((z) => (
                      <option
                        key={z.id_modoContratacion}
                        value={z.id_modoContratacion}
                      >
                        {z.descMcontratacion}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tiempo en la Empresa (años) *</label>
                <input
                  name="tiempoEmpresa"
                  value={form.tiempoEmpresa}
                  onChange={handleChange}
                  placeholder="Ej: 2"
                  maxLength="2"
                  required
                  title="Solo números de hasta 2 dígitos"
                />
              </div>

              <div className="form-group">
                <label>Fecha Ingreso Sindicato</label>
                <input
                  type="date"
                  value={obtenerFechaActual()}
                  readOnly
                  style={{
                    backgroundColor: "#f0f0f0",
                    cursor: "not-allowed",
                  }}
                  title="Fecha automática del día de diligenciamiento"
                />
              </div>
            </div>
          </div>

          {/* FIRMA */}
          <div className="form-section firma-section">
            <h2 className="section-title">
              <span className="icon">✍️</span> Firma del Afiliado
            </h2>
            <p className="firma-instruccion">
              Firme en el recuadro usando su mouse o dispositivo táctil
            </p>
            <div ref={sigWrapper} className="firma-container">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="#003366"
                canvasProps={{
                  className: "signature-canvas",
                }}
                onEnd={() => {
                  if (!sigCanvas.current.isEmpty()) {
                    setFirmaCompletada(true);
                  }
                }}
              />
            </div>
            <div className="firma-botones">
              <button
                type="button"
                onClick={guardarFirma}
                className="btn-firma btn-guardar"
              >
                ✓ Confirmar Firma
              </button>
              <button
                type="button"
                onClick={limpiarFirma}
                className="btn-firma btn-limpiar"
              >
                ✕ Limpiar
              </button>
            </div>
            {firmaCompletada && (
              <div className="firma-ok">✓ Firma registrada correctamente</div>
            )}
          </div>

          {/* BOTÓN ENVIAR */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn-enviar"
              disabled={generandoPDF}
            >
              {generandoPDF ? (
                <>
                  <span className="spinner"></span> Generando PDF...
                </>
              ) : (
                <>📥 Registrar y Descargar Formulario</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Formulario;